import { Col, Form, message, Row } from "antd";
import dayjs, { Dayjs } from "dayjs";
import React, { useEffect, useRef } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useMasterConfigSelect } from "../../../../../hooks/useMasterConfigSelect";
import { AppDispatch } from "../../../../../redux/store";
import { UsageType } from "../../../../../types/bookings";
import { SelectOption } from "../../../../../types/select";
import { DateField, InputField, ReactSelectField } from "../../../../atoms/FormElement";
import { InputNumberFieldV2 as InputNumberField } from "../../../../atoms/FormElement/InputNumberField";
import { findSelectOption, getSelectPlaceholderMessage } from "../../../../libs/form";
import { convertToDayjs, DATE_FORMATS, formatDateByType } from "../../../../libs/utils";
import { CommonCard } from "../../components";
import { getBookingDetail, setBookingForm, setFormState, updateBooking } from "../../ducks/actions";
import { useGetLeaseDuration } from "../../hooks/useGetLeaseDuration";
import { useGetOrganization } from "../../../../../api/v1/organizations/useGetOrganization";
import { torRulesOption } from "../../utils";
import { LeaseEndDateRefType, NumberOfYearsSelect } from "../NumberOfYearsSelect";
import Parkings from "../Parkings";
import {
  calculateLeaseEndDate,
  calculateRentFree,
  calculateYears,
  disableBookingEndDate,
  disableFitOutEndDate,
  disableFitOutStartDate,
  disableLeaseEndDate,
  disableLeaseStartDate,
  disablePreviousDate,
  excludedLeaseTermsKeys,
  filterDropdownValue,
  generateYearOptions,
  parkingPayload,
  rentFreePeriodValues,
} from "../utils";
import { invoicingPatternOptions, InvoicingPatternSelect } from "./InvoicingPatternSelect";

export type LeaseTermFormData = {
  leaseManager?: string;
  externalAgent?: string;
  leaseNature?: SelectOption;
  bookingStartDate?: string | Dayjs;
  bookingEndDate?: string | Dayjs;
  fitOutStartDate?: string | Dayjs;
  fitOutEndDate?: string | Dayjs;
  leaseStartDate?: string | Dayjs;
  leaseEndDate?: string | Dayjs;
  numberOfYears: SelectOption;
  rentFreePeriod?: SelectOption;
  invoicingPattern?: SelectOption;
  moveInDate?: string | Dayjs;
  parkings?: unknown[];
  noOfFreeParkingSlots: number;
  commencementDate?: string | Dayjs;
  leaseDuration?: string;
};

interface IProps {
  parentState: (obj: object) => void;
}

const index = (props: IProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { bookingForm, landlordUsers, bookingDetails } = useSelector((state: any) => state.newBooking);

  const leaseEndDateRef = useRef<LeaseEndDateRefType>({ actual: null, withRentFreePeriod: null });

  const { selectOptions: leaseNatureOptions, isSuccessMasterConfig } = useMasterConfigSelect({
    configItem: "leaseNature",
    filterBy: "usageType",
    value: String(bookingDetails?.propertyDetails?.usagesType).toLowerCase() as UsageType,
    isEnabled: Boolean(bookingDetails?.propertyDetails?.usagesType),
  });

  const { leaseId = bookingDetails.leaseId } = useParams();
  const yearsOptions = generateYearOptions({ start: 1, end: 10, withCustomOption: true });
  const rentFreePeriodOptions = rentFreePeriodValues() as SelectOption[];

  const methods = useForm<LeaseTermFormData>({
    defaultValues: {
      leaseManager: filterDropdownValue(bookingForm?.leaseManager?.userId || bookingForm?.leaseManager, landlordUsers),
      externalAgent: bookingForm?.externalAgent || "",
      bookingStartDate: convertToDayjs(bookingForm?.bookingTerms?.bookingStartDate),
      // || dateFormat(dayjs(currentDate)),
      bookingEndDate: convertToDayjs(bookingForm?.bookingTerms?.bookingEndDate),
      //  || dateFormat(dayjs(currentDate).add(29, "day")),
      fitOutStartDate: convertToDayjs(bookingForm?.leaseTerms?.fitOutStartDate),
      fitOutEndDate: convertToDayjs(bookingForm?.leaseTerms?.fitOutEndDate),
      leaseStartDate: convertToDayjs(bookingForm?.leaseTerms?.leaseStartDate),
      // || dateFormat(dayjs(currentDate).add(29, "day")),
      leaseEndDate: convertToDayjs(bookingForm?.leaseTerms?.leaseEndDate),
      // || dateFormat(dayjs(currentDate).add(29, "day")),
      // calculateLeaseEndDate(dayjs(currentDate).add(29, "day"), 1),
      commencementDate: convertToDayjs(
        bookingForm?.leaseTerms?.commencementDate || bookingForm?.leaseTerms?.leaseStartDate
      ),
      numberOfYears:
        findSelectOption(yearsOptions, bookingForm?.leaseTerms?.numberOfYears) ??
        calculateYears(bookingForm?.leaseTerms?.leaseStartDate, bookingForm?.leaseTerms?.leaseEndDate) ??
        yearsOptions[0],
      rentFreePeriod:
        findSelectOption(rentFreePeriodOptions, bookingForm?.leaseTerms?.rentFreePeriod) ??
        calculateRentFree(bookingForm?.leaseTerms?.rentFreeStartDate, bookingForm?.leaseTerms?.rentFreeEndDate) ??
        rentFreePeriodOptions[0],
      invoicingPattern: findSelectOption(
        invoicingPatternOptions,
        bookingForm?.invoicingPattern ?? bookingDetails?.invoicingPattern
      ),
      moveInDate: convertToDayjs(bookingForm?.leaseTerms?.moveInDate),
      // ||
      // dateFormat(bookingForm?.bookingTerms?.bookingEndDate) ||
      // dateFormat(dayjs(currentDate).add(29, "day")),
      noOfFreeParkingSlots: bookingForm?.leaseTerms?.noOfFreeParkingSlots,
      parkings: bookingForm?.parkings || [],
    } satisfies LeaseTermFormData,
  });

  const {
    control,
    setValue,
    setError,
    setFocus,
    getValues,
    clearErrors,
    handleSubmit,
    watch,
    formState: { errors },
  } = methods;

  const parkingsWatcher = useWatch({ control, name: "parkings" });
  const watchedFormValues = watch();
  const isCustomNumberOfYears = watchedFormValues?.numberOfYears?.value === "custom";

  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (name) clearErrors(name as keyof LeaseTermFormData);
    });
    return () => subscription.unsubscribe();
  }, [watch, clearErrors]);

  useEffect(() => {
    leaseEndDateRef.current.actual = dayjs(
      bookingDetails?.leaseTerms?.rentFreeStartDate ?? bookingDetails?.leaseTerms?.leaseEndDate
    );
  }, [bookingDetails?.leaseTerms?.rentFreeStartDate, bookingDetails?.leaseTerms?.leaseEndDate]);

  useEffect(() => {
    if (!bookingForm?.leaseTerms?.leaseNature || !isSuccessMasterConfig) return;

    const selectedLeaseNature = findSelectOption(leaseNatureOptions, bookingForm.leaseTerms.leaseNature);
    if (!selectedLeaseNature) return;

    setValue("leaseNature", selectedLeaseNature);
  }, [bookingForm?.leaseTerms?.leaseNature, isSuccessMasterConfig]);

  useEffect(() => {
    bookingForm?.parkings?.length > 0 && setValue("parkings", bookingForm?.parkings);
  }, [bookingForm?.parkings]);

  let torExist = torRulesOption?.find((item) => item?.value === (bookingForm?.torType ?? bookingDetails?.torType));

  const onSubmit = async (data, type) => {
    clearErrors();

    let dataCopy = { ...(data || getValues()) };

    let body = {
      leaseManager: dataCopy.leaseManager?.value,
      externalAgent: dataCopy.externalAgent,
      invoicingPattern: dataCopy.invoicingPattern?.value,
      leaseTerms: {
        leaseType: "new",
        leaseNature: dataCopy.leaseNature?.value,
        moveInDate: formatDateByType(dataCopy.moveInDate, "api"),
        leaseEndDate: formatDateByType(dataCopy.leaseEndDate, "api"),
        leaseStartDate: formatDateByType(dataCopy.leaseStartDate, "api"),
        numberOfYears: dataCopy?.numberOfYears?.value,
        fitOutStartDate: formatDateByType(dataCopy.fitOutStartDate, "api"),
        noOfFreeParkingSlots: dataCopy?.noOfFreeParkingSlots === "" ? 0 : Number(dataCopy?.noOfFreeParkingSlots),
        fitOutEndDate: formatDateByType(dataCopy.fitOutEndDate, "api"),
        rentFreePeriod: dataCopy?.rentFreePeriod?.value,
        commencementDate: formatDateByType(dataCopy.commencementDate, "api"),
        ...(parseInt(dataCopy?.rentFreePeriod?.value) > 0
          ? {
              rentFreeStartDate: formatDateByType(leaseEndDateRef.current.actual, "api"),
              rentFreeEndDate: formatDateByType(leaseEndDateRef.current.withRentFreePeriod, "api"),
            }
          : {}),
      },
      torType: dataCopy?.torRuleType?.value,
      bookingTerms: {
        ...bookingForm?.bookingTerms,
        bookingEndDate: formatDateByType(dataCopy.bookingEndDate, "api"),
        bookingStartDate: formatDateByType(dataCopy.bookingStartDate, "api"),
      },
      parkings: parkingPayload(dataCopy?.parkings),
    };

    if (bookingDetails?.torRules && bookingDetails?.torRules?.length > 0) {
      body["torRules"] = bookingDetails?.torRules;
    }

    if (type === "next") {
      let valid = true;

      Object.keys(dataCopy).forEach((item) => {
        const key = item as keyof LeaseTermFormData;
        if (!excludedLeaseTermsKeys.includes(key)) {
          const value = dataCopy[key];
          if (value) return;

          setError(key, { type: "manual", message: `Please enter ${key.replace(/([A-Z])/g, " $1").toLowerCase()}` });
          setFocus(key);
          valid = false;
        }
      });

      if (!valid) return;

      dispatch(setFormState());
      dispatch(setBookingForm(body));
    }
      body = { ...bookingForm, ...body };

      props.parentState({ load: true });

      try {
        const response = await dispatch(updateBooking({ leaseId, body }));

        await dispatch(getBookingDetail(leaseId));

        message.success(response.data.message);
      } catch (error) {
        message.error(error.response?.data?.error || error.response?.data?.message);
      } finally {
        props.parentState({ load: false });
    }
  };

  const { label } = useGetLeaseDuration(watchedFormValues?.leaseStartDate, watchedFormValues?.leaseEndDate);
  const { organization } = useGetOrganization();

  return (
    <FormProvider {...methods}>
      <Form
        layout="vertical"
        name="bookingform"
        scrollToFirstError
        className="bf-main"
        onFinish={handleSubmit(onSubmit)}
      >
        <CommonCard
          label="Lease Terms"
          leftBtnLabel="Back"
          rightBtnLabel="Next"
          handleNext={onSubmit}
          parentState={props.parentState}
          handleBack={() => dispatch(setFormState("back"))}
        >
          <Col span={24}>
            <Row gutter={24}>
              <Col span={8}>
                <ReactSelectField
                  control={control}
                  isRequired={true}
                  setValue={setValue}
                  label="Lease Executive"
                  fieldname="leaseManager"
                  selectOption={landlordUsers}
                  iProps={{ placeholder: getSelectPlaceholderMessage("Lease Executive") }}
                  rules={{ required: "Please select one" }}
                  validate={errors.leaseManager && "error"}
                  validMessage={errors.leaseManager && errors.leaseManager.message}
                />
              </Col>

              <Col span={8}>
                <InputField
                  label="External Agent"
                  fieldname="externalAgent"
                  control={control}
                  initValue=""
                  setValue={setValue}
                  iProps={{ placeholder: "Enter External Agent" }}
                />
              </Col>

              <Col span={8}>
                <ReactSelectField
                  isRequired={true}
                  control={control}
                  setValue={setValue}
                  label="Lease Nature"
                  fieldname="leaseNature"
                  selectOption={leaseNatureOptions}
                  iProps={{ placeholder: getSelectPlaceholderMessage("Lease Nature") }}
                  validate={errors.leaseNature && "error"}
                  rules={{ required: "Please select lease nature" }}
                  validMessage={errors.leaseNature && errors.leaseNature.message}
                />
              </Col>

              <Col span={5}>
                <DateField
                  isRequired={true}
                  control={control}
                  setValue={setValue}
                  label="Booking Start Date"
                  fieldname="bookingStartDate"
                  rules={{ required: "Please select date" }}
                  validate={errors.bookingStartDate && "error"}
                  iProps={{
                    format: DATE_FORMATS.display,
                    placeholder: DATE_FORMATS.display,
                    disabledDate: disablePreviousDate,
                  }}
                  onChange={(date) => {
                    if (date) {
                      {
                        setValue("bookingEndDate", dayjs(date).add(29, "day"));
                        setValue("moveInDate", dayjs(date).add(29, "day"));
                        // setValue("leaseStartDate", dayjs(date).add(29, "day"));
                      }
                    } else setValue("bookingEndDate", "");
                  }}
                  validMessage={errors.bookingStartDate && errors.bookingStartDate.message}
                />
              </Col>

              <Col span={5}>
                <DateField
                  control={control}
                  isRequired={true}
                  setValue={setValue}
                  label="Booking End date"
                  fieldname="bookingEndDate"
                  rules={{ required: "Please select date" }}
                  validate={errors.bookingEndDate && "error"}
                  // onChange={(date) => {
                  //   setValue("moveInDate", date);
                  // }}
                  iProps={{
                    format: DATE_FORMATS.display,
                    placeholder: DATE_FORMATS.display,
                    disabledDate: (date) =>
                      disableBookingEndDate(date, getValues("bookingStartDate"), getValues("bookingEndDate")),
                  }}
                  validMessage={errors.bookingEndDate && errors.bookingEndDate.message}
                />
              </Col>
              <Col span={4}>
                <DateField
                  control={control}
                  isRequired={true}
                  setValue={setValue}
                  label="Move-in / Handover Date"
                  fieldname="moveInDate"
                  disabled={!watchedFormValues?.leaseStartDate}
                  rules={{ required: "Enter Date" }}
                  validate={errors?.moveInDate && "error"}
                  validMessage={errors?.moveInDate && errors?.moveInDate.message}
                  onChange={() => !watchedFormValues?.leaseStartDate && setValue("leaseStartDate", null)}
                  iProps={{
                    format: DATE_FORMATS.display,
                    placeholder: DATE_FORMATS.display,
                  }}
                />
              </Col>
              <Col span={5}>
                <DateField
                  control={control}
                  setValue={setValue}
                  label="Fit-out Start Date"
                  fieldname="fitOutStartDate"
                  iProps={{
                    format: DATE_FORMATS.display,
                    placeholder: DATE_FORMATS.display,
                    disabledDate: (date) =>
                      disableFitOutStartDate(
                        date,
                        undefined,
                        getValues("leaseStartDate"),
                        getValues("bookingStartDate")
                      ),
                  }}
                />
              </Col>

              <Col span={5}>
                <DateField
                  control={control}
                  setValue={setValue}
                  label="Fit-out End date"
                  fieldname="fitOutEndDate"
                  onChange={(date) => {
                    if (date) {
                      const leaseStartDate = dayjs(date).add(1, "day");
                      setValue("leaseStartDate", leaseStartDate);
                      const leaseEndDate = calculateLeaseEndDate(leaseStartDate, 1);
                      leaseEndDateRef.current.actual = leaseEndDate;
                      setValue("leaseEndDate", leaseEndDate);
                      setValue("commencementDate", leaseStartDate);
                      setValue("moveInDate", leaseStartDate);
                    } else {
                      setValue("leaseStartDate", null);
                      setValue("commencementDate", null);
                      setValue("leaseEndDate", null);
                      setValue("moveInDate", null);
                      leaseEndDateRef.current.actual = null;
                    }

                    setValue("numberOfYears", yearsOptions[0]);
                    setValue("rentFreePeriod", null);
                    leaseEndDateRef.current.withRentFreePeriod = null;
                  }}
                  iProps={{
                    format: DATE_FORMATS.display,
                    placeholder: DATE_FORMATS.display,
                    disabledDate: (date) =>
                      disableFitOutEndDate(date, undefined, getValues("leaseStartDate"), getValues("fitOutStartDate")),
                  }}
                />
              </Col>

              <Col span={5}>
                <DateField
                  control={control}
                  isRequired={true}
                  setValue={setValue}
                  label="Lease Start Date"
                  fieldname="leaseStartDate"
                  validate={errors.leaseStartDate && "error"}
                  rules={{
                    required: "Enter lease start date",
                  }}
                  onChange={(date) => {
                    if (date) {
                      const leaseEndDate = calculateLeaseEndDate(date, 1);

                      leaseEndDateRef.current.actual = leaseEndDate;
                      setValue("leaseEndDate", leaseEndDate);
                      setValue("moveInDate", leaseEndDate);
                      setValue("commencementDate", date);
                    } else {
                      leaseEndDateRef.current.actual = null;
                      setValue("leaseEndDate", null);
                      setValue("moveInDate", null);
                    }

                    setValue("numberOfYears", yearsOptions[0]);
                    setValue("rentFreePeriod", null);
                    leaseEndDateRef.current.withRentFreePeriod = null;
                  }}
                  iProps={{
                    format: DATE_FORMATS.display,
                    placeholder: DATE_FORMATS.display,
                    disabledDate: (date) =>
                      disableLeaseStartDate(
                        date,
                        getValues("bookingStartDate"),
                        organization?.leaseStartDatePastDaysLimit,
                      ),
                  }}
                  validMessage={errors.leaseStartDate && errors.leaseStartDate.message}
                />
              </Col>

              <Col span={4}>
                <NumberOfYearsSelect
                  options={yearsOptions}
                  isDisabled={!watchedFormValues?.leaseStartDate}
                  leaseEndDateRef={leaseEndDateRef}
                />
              </Col>

              <Col span={4}>
                <DateField
                  control={control}
                  isRequired={true}
                  setValue={setValue}
                  label="Lease End Date"
                  fieldname="leaseEndDate"
                  disabled={!watchedFormValues?.leaseStartDate || !isCustomNumberOfYears}
                  validate={errors.leaseEndDate && "error"}
                  rules={{
                    required: "Enter lease end date",
                  }}
                  onChange={(date) => {
                    if (date) {
                      leaseEndDateRef.current.actual = date;
                    }
                    setValue("rentFreePeriod", null);
                    leaseEndDateRef.current.withRentFreePeriod = null;
                  }}
                  iProps={{
                    format: DATE_FORMATS.display,
                    placeholder: DATE_FORMATS.display,
                    disabledDate: (date) =>
                      disableLeaseEndDate(date, getValues("leaseStartDate"), isCustomNumberOfYears),
                  }}
                  validMessage={errors.leaseEndDate && errors.leaseEndDate.message}
                />
              </Col>
              <Col span={5}>
                <InputField
                  label="Lease Duration"
                  fieldname="leaseDuration"
                  control={control}
                  initValue={label}
                  disabled={true}
                  setValue={setValue}
                  valueGot={label}
                  iProps={{ placeholder: "Lease Duration" }}
                />
              </Col>
              <Col span={5}>
                <ReactSelectField
                  control={control}
                  setValue={setValue}
                  label="Rent Free Period"
                  fieldname="rentFreePeriod"
                  isDisabled={!watchedFormValues?.leaseStartDate}
                  selectOption={rentFreePeriodOptions}
                  onChange={(e) => {
                    const extendedEndDate = dayjs(leaseEndDateRef.current.actual).add(parseInt(e.value), "days");

                    setValue("leaseEndDate", extendedEndDate, { shouldDirty: true, shouldValidate: true });
                    leaseEndDateRef.current.withRentFreePeriod = extendedEndDate;
                  }}
                  iProps={{ placeholder: getSelectPlaceholderMessage("Rent Free Period") }}
                />
              </Col>

              <Col span={5}>
                <DateField
                  control={control}
                  isRequired={true}
                  setValue={setValue}
                  label="Rent Commencement Date"
                  fieldname="commencementDate"
                  validate={errors.commencementDate && "error"}
                  rules={{
                    required: "Enter Commencement Date",
                  }}
                  iProps={{
                    format: DATE_FORMATS.display,
                    placeholder: DATE_FORMATS.display,
                  }}
                  validMessage={errors.commencementDate && errors.commencementDate.message}
                />
              </Col>

              <Col span={4}>
                <InputNumberField
                  label="Number of parking slots"
                  fieldname={`noOfFreeParkingSlots`}
                  control={control}
                />
              </Col>

              <Col span={4}>
                <InvoicingPatternSelect />
              </Col>

              <Col span={4}>
                <ReactSelectField
                  control={control}
                  isRequired={false}
                  setValue={setValue}
                  label="Lease Type"
                  fieldname="torRuleType"
                  selectOption={torRulesOption}
                  valueGot={torExist ? torExist : torRulesOption[0]}
                  onChange={(e) => localStorage?.setItem("torType", e?.value)}
                  iProps={{ placeholder: "Select" }}
                />
              </Col>
            </Row>

            {/* third row */}

            <Row gutter={24}>
              <Col span={24} style={{ marginTop: "20px" }}>
                <p className="ft-main-label">Parking</p>
                <Parkings
                  data={parkingsWatcher}
                  setParkingValue={(data) => setValue("parkings", data)}
                  noOfFreeParkingSlots={watchedFormValues?.noOfFreeParkingSlots}
                />
              </Col>
            </Row>
          </Col>
        </CommonCard>
      </Form>
    </FormProvider>
  );
};

export default index;
